'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { Building2, Link2, PenLine, FolderOpen, ShieldCheck, ArrowRight, ChevronRight, CheckCircle2, AlertTriangle, Clock, CheckSquare } from 'lucide-react';
import HomeIntroSplash from './components/HomeIntroSplash';

// ─── Shared heading style ──────────────────────────────────────────────
const headingFont: React.CSSProperties = { fontFamily: 'var(--font-inter-tight), system-ui, sans-serif' };

// ─── Reveal animation hook ─────────────────────────────────────────────
function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect prefers-reduced-motion
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, visible };
}

// Hero-specific reveal: starts visible so ProductPreview is never hidden on load
function useHeroReveal() {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(true); // always visible — no IntersectionObserver hide

  useEffect(() => {
    // Still trigger a subtle entrance animation after mount
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return;
    setVisible(false);
    const t = requestAnimationFrame(() => {
      requestAnimationFrame(() => setVisible(true));
    });
    return () => cancelAnimationFrame(t);
  }, []);

  return { ref, visible };
}

// Reveal style helper
function revealStyle(visible: boolean, delay = 0): React.CSSProperties {
  return {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(10px)',
    transition: `opacity 220ms ease ${delay}ms, transform 220ms ease ${delay}ms`,
  };
}

// ─── Header ───────────────────────────────────────────────────────────

function LandingHeader() {
  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200" role="banner">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[66px]">
          {/* Brand lockup — navy icon + typographic lockup */}
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'var(--primary)' }}
              aria-hidden="true"
            >
              <Building2 size={16} className="text-white" />
            </div>
            <div>
              <span className="text-[17px] font-semibold text-slate-900 leading-tight block tracking-[-0.03em]" style={headingFont}>PlenariCAT</span>
              <span className="text-[11px] text-slate-500 leading-tight hidden sm:block" style={{ fontFamily: 'var(--font-dm-sans), system-ui, sans-serif' }}>Treball municipal</span>
            </div>
          </div>

          {/* Nav links — desktop */}
          <nav className="hidden md:flex items-center gap-6" aria-label="Navegació principal">
            <a href="#funcionalitats" className="text-[13px] text-slate-600 hover:text-slate-900 transition-colors">
              Funcionalitats
            </a>
            <a href="#com-funciona" className="text-[13px] text-slate-600 hover:text-slate-900 transition-colors">
              Com funciona
            </a>
            <a href="#per-a-qui" className="text-[13px] text-slate-600 hover:text-slate-900 transition-colors">
              Per a qui
            </a>
            <a href="#seguretat" className="text-[13px] text-slate-600 hover:text-slate-900 transition-colors">
              Transparència
            </a>
          </nav>

          {/* CTAs */}
          <div className="flex items-center gap-2">
            <Link
              href="/login"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium text-slate-700 border border-slate-300 rounded-lg transition-all hover:bg-slate-50 hover:border-slate-400 min-h-[44px]"
            >
              Accedeix
            </Link>
            <Link
              href="/demo"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium text-white rounded-lg transition-all hover:opacity-90 hover:-translate-y-px min-h-[44px]"
              style={{ backgroundColor: 'var(--primary)' }}
            >
              <span className="hidden xs:inline">Demana una demo</span>
              <span className="xs:hidden">Demo</span>
              <ChevronRight size={13} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

// ─── Product Preview ──────────────────────────────────────────────────

function ProductPreview() {
  return (
    <div
      className="rounded-[10px] border border-slate-200/80 bg-white overflow-hidden"
      style={{ boxShadow: '0 4px 24px rgba(15,23,42,0.10), 0 1px 4px rgba(15,23,42,0.06)' }}
      role="img"
      aria-label="Previsualització de la interfície de PlenariCAT: dossier de ple amb punts de l'ordre del dia, alertes documentals i estat de revisió"
    >
      <div className="flex" style={{ minHeight: '380px' }}>
        {/* Sidebar */}
        <div className="hidden sm:flex flex-col w-52 bg-slate-900 p-3 gap-0.5 flex-shrink-0" aria-hidden="true">
          {/* Org / municipality header */}
          <div className="px-2 py-3 mb-2 border-b border-slate-700/60">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0">
                <Building2 size={13} className="text-white/70" />
              </div>
              <div>
                <div className="text-[12px] font-semibold text-white leading-tight">Berga</div>
                <div className="text-[10px] text-slate-400 leading-tight">Berguedà</div>
              </div>
            </div>
          </div>
          {[
            { label: 'Tauler', active: false },
            { label: 'Sessions plenàries', active: true },
            { label: 'Assumptes', active: false },
            { label: 'Redacció', active: false },
            { label: 'Cerca', active: false },
          ]?.map((item) => (
            <div
              key={item?.label}
              className={`flex items-center gap-2 px-2.5 py-2 rounded text-[11px] ${item?.active ? 'bg-white/10 text-white font-semibold' : 'text-slate-400 hover:text-slate-300'}`}
            >
              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${item?.active ? 'bg-white' : 'bg-slate-600'}`} />
              {item?.label}
            </div>
          ))}

          {/* Document status markers */}
          <div className="mt-auto pt-4 border-t border-slate-700/60 space-y-1.5">
            <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-widest px-2">ESTAT</div>
            {[
              { label: 'REVISAT', color: 'bg-emerald-500', count: '3' },
              { label: 'PENDENT', color: 'bg-amber-400', count: '4' },
              { label: 'ALERTA', color: 'bg-red-400', count: '2' },
            ]?.map((s) => (
              <div key={s.label} className="flex items-center justify-between px-2">
                <div className="flex items-center gap-1.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${s.color}`} />
                  <span className="text-[9px] font-mono text-slate-400 tracking-wider">{s.label}</span>
                </div>
                <span className="text-[10px] font-mono text-slate-500">{s.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col bg-slate-50 overflow-hidden" aria-hidden="true">
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 sm:px-5 py-3.5 bg-white border-b border-slate-200">
            <div>
              <div className="text-[13px] font-semibold text-slate-900 leading-tight">Ple Ordinari — Agost 2026</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Berga · 28 d&apos;agost de 2026 · 19:00 h</div>
            </div>
            <div className="flex items-center gap-2">
              {/* Amber alert — semantic */}
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-amber-50 text-amber-700 border border-amber-200 font-medium">
                <AlertTriangle size={10} />
                3 alertes
              </span>
              {/* Navy/slate progress — semantic */}
              <span className="hidden sm:inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-slate-100 text-slate-600 border border-slate-200 font-medium">
                <Clock size={10} />
                33% revisat
              </span>
            </div>
          </div>

          {/* Progress bar — navy */}
          <div className="h-[3px] bg-slate-200 overflow-hidden">
            <div className="h-full w-1/3" style={{ backgroundColor: 'var(--primary)' }} />
          </div>

          {/* Agenda items list */}
          <div className="flex-1 p-4 sm:p-5 space-y-2 overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Ordre del dia · 9 punts</span>
              <span className="text-[10px] font-mono text-slate-400">DOCUMENT 01</span>
            </div>
            {[
              { num: '01', label: "Aprovació de l'acta anterior", status: 'REVISAT', statusColor: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckSquare, iconColor: 'text-emerald-600' },
              { num: '02', label: 'Modificació pressupostària 2/2026', status: 'ALERTA ⚠', statusColor: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle, iconColor: 'text-amber-500', alert: true },
              { num: '03', label: 'Ordenança reguladora de terrasses', status: 'EN REVISIÓ', statusColor: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock, iconColor: 'text-slate-400' },
              { num: '04', label: 'Adjudicació neteja viària 2026–2029', status: 'PENDENT', statusColor: 'bg-slate-100 text-slate-500 border-slate-200', icon: Clock, iconColor: 'text-slate-300' },
              { num: '05', label: 'Conveni Diputació de Barcelona', status: 'PENDENT', statusColor: 'bg-slate-100 text-slate-500 border-slate-200', icon: Clock, iconColor: 'text-slate-300' },
            ]?.map((item) => {
              const ItemIcon = item?.icon;
              return (
                <div key={item?.label} className={`flex items-center gap-3 bg-white rounded-lg border px-3 py-2.5 ${item?.alert ? 'border-amber-200 border-l-2 border-l-amber-400' : 'border-slate-100'}`}>
                  <span className="text-[10px] font-mono text-slate-400 w-5 flex-shrink-0">{item?.num}</span>
                  <ItemIcon size={11} className={`flex-shrink-0 ${item?.iconColor}`} />
                  <span className="text-[12px] text-slate-700 flex-1 min-w-0 truncate">{item?.label}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded border font-semibold flex-shrink-0 font-mono tracking-wide ${item?.statusColor}`}>
                    {item?.status}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Bottom doc bar — emerald verified */}
          <div className="px-4 sm:px-5 py-2.5 bg-white border-t border-slate-100 flex items-center justify-between">
            <span className="text-[10px] font-mono text-slate-400">FONT · Convocatòria oficial · pàg. 1–12</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-600 font-semibold">
              <CheckCircle2 size={9} className="text-emerald-500" />
              FONT VERIFICADA
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────

function HeroSection() {
  const { ref, visible } = useHeroReveal();

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="relative overflow-hidden px-4 sm:px-6 lg:px-8"
      style={{ paddingTop: '64px', paddingBottom: '72px', backgroundColor: '#f8f9fb' }}
      aria-labelledby="hero-heading"
    >
      {/* Subtle documentary grid background — linear-gradient only, no radial, no blobs */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(to right, rgba(148,163,184,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(148,163,184,0.08) 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }} />
        {/* Thin structural lines only */}
        <div className="absolute top-16 right-[8%] w-px h-32 bg-slate-300/30" />
        <div className="absolute top-8 right-[12%] w-16 h-px bg-slate-300/30" />
        <div className="absolute bottom-16 left-[4%] w-px h-24 bg-slate-300/20" />
      </div>

      <div className="max-w-[1280px] mx-auto relative">
        {/* Stack on lg and below, side-by-side at xl+ */}
        <div className="grid grid-cols-1 xl:grid-cols-[45fr_55fr] gap-10 xl:gap-16 items-center">

          {/* Left — content */}
          <div style={revealStyle(visible, 0)}>
            {/* Eyebrow — amber institutional filet */}
            <div className="flex items-center gap-3 mb-5">
              <div className="w-5 h-px" style={{ backgroundColor: '#d97706' }} aria-hidden="true" />
              <p className="text-[11px] font-semibold tracking-[0.15em] uppercase text-slate-500" style={{ fontFamily: 'var(--font-dm-sans), system-ui, sans-serif' }}>
                Plataforma de treball municipal
              </p>
            </div>

            <h1
              id="hero-heading"
              className="font-semibold text-slate-900 tracking-[-0.02em] mb-5 text-balance"
              style={{
                ...headingFont,
                fontSize: 'clamp(2.5rem, 5vw, 4.25rem)',
                lineHeight: '1.04',
              }}
            >
              El teu ple, preparat.
              <br />
              Cada font, traçable.
            </h1>

            {/* Emerald documental marker — under second H1 line */}
            <div className="flex items-center gap-2 mb-5 -mt-3" aria-hidden="true">
              <div
                style={{
                  width: '48px',
                  height: '2px',
                  backgroundColor: '#10b981',
                  borderRadius: '1px',
                }}
              />
            </div>

            <p className="text-[1.0625rem] sm:text-lg text-slate-600 leading-relaxed mb-3 max-w-lg">
              PlenariCAT ordena convocatòries, actes i annexos, connecta antecedents i centralitza la preparació, redacció i seguiment del treball municipal.
            </p>

            <p className="text-[0.9375rem] text-slate-500 leading-relaxed mb-7 max-w-md">
              Per a grups municipals i organitzacions amb implantació territorial.
            </p>

            <div className="flex flex-col xs:flex-row gap-3 mb-5">
              <Link
                href="/login"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[0.9375rem] font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all min-h-[48px]"
              >
                Explora la demostració
              </Link>
            </div>

            {/* Discrete microcopy — no button appearance */}
            <p
              className="text-[0.75rem] text-slate-500 mb-7"
              style={{ fontFamily: 'var(--font-dm-sans), system-ui, sans-serif' }}
            >
              Vols una demo guiada? La pots demanar des del botó superior.
            </p>

            {/* Trust micro-indicators — coloured dots */}
            <div className="flex flex-wrap gap-x-5 gap-y-2.5" style={{ fontFamily: 'var(--font-dm-sans), system-ui, sans-serif' }}>
              {/* Emerald: verified / source / review */}
              <div className="flex items-center gap-1.5">
                <span
                  className="flex-shrink-0 rounded-full"
                  style={{ width: '6px', height: '6px', backgroundColor: '#10b981' }}
                  aria-hidden="true"
                />
                <span className="text-[11px] font-semibold text-slate-700 tracking-wide">Fonts verificables</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span
                  className="flex-shrink-0 rounded-full"
                  style={{ width: '6px', height: '6px', backgroundColor: '#10b981' }}
                  aria-hidden="true"
                />
                <span className="text-[11px] font-semibold text-slate-700 tracking-wide">Revisió humana</span>
              </div>
              {/* Slate: no citizen profiling */}
              <div className="flex items-center gap-1.5">
                <span
                  className="flex-shrink-0 rounded-full"
                  style={{ width: '6px', height: '6px', backgroundColor: '#64748b' }}
                  aria-hidden="true"
                />
                <span className="text-[11px] font-medium text-slate-600 tracking-wide">Sense perfilat de ciutadans</span>
              </div>
              {/* Amber: demo / pending */}
              <div className="flex items-center gap-1.5">
                <span
                  className="flex-shrink-0 rounded-full"
                  style={{ width: '6px', height: '6px', backgroundColor: '#d97706' }}
                  aria-hidden="true"
                />
                <span className="text-[11px] font-medium text-slate-500 tracking-wide">Dades demo simulades</span>
              </div>
            </div>
          </div>

          {/* Right — ProductPreview with slate-100 backdrop */}
          <div className="relative" style={revealStyle(visible, 60)}>
            {/* Slate-100 rectangular backdrop — no gradients, no blobs */}
            <div
              className="absolute -inset-5 rounded-lg border border-slate-200"
              style={{ backgroundColor: '#f1f5f9' }}
              aria-hidden="true"
            />
            {/* Corner markers — documentary style, more visible */}
            <div className="absolute -top-3 -left-3 w-5 h-5 border-t-2 border-l-2 border-slate-400/50 rounded-tl-sm" aria-hidden="true" />
            <div className="absolute -top-3 -right-3 w-5 h-5 border-t-2 border-r-2 border-slate-400/50 rounded-tr-sm" aria-hidden="true" />
            <div className="absolute -bottom-3 -left-3 w-5 h-5 border-b-2 border-l-2 border-slate-400/50 rounded-bl-sm" aria-hidden="true" />
            <div className="absolute -bottom-3 -right-3 w-5 h-5 border-b-2 border-r-2 border-slate-400/50 rounded-br-sm" aria-hidden="true" />
            {/* Subtle grid overlay on backdrop */}
            <div
              className="absolute -inset-5 rounded-lg pointer-events-none"
              style={{
                backgroundImage: 'linear-gradient(to right, rgba(148,163,184,0.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(148,163,184,0.07) 1px, transparent 1px)',
                backgroundSize: '40px 40px',
              }}
              aria-hidden="true"
            />
            <div className="relative">
              <ProductPreview />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Problem section ──────────────────────────────────────────────────

function ProblemSection() {
  const { ref, visible } = useReveal();
  const problems = [
    {
      key: 'prob-1',
      num: '01',
      title: 'Documents dispersos',
      text: 'Convocatòries, annexos i informes tècnics arriben per canals separats, sense estructura ni context.',
    },
    {
      key: 'prob-2',
      num: '02',
      title: 'Poc temps per revisar',
      text: 'Els dossiers arriben pocs dies abans del ple. Revisar desenes de pàgines en profunditat és inviable.',
    },
    {
      key: 'prob-3',
      num: '03',
      title: 'Antecedents difícils de recuperar',
      text: 'Cada punt té un historial de decisions i documents anteriors que cal rastrejar manualment.',
    },
    {
      key: 'prob-4',
      num: '04',
      title: 'Seguiment fragmentat',
      text: 'Preguntes, precs i compromisos queden repartits entre correus, Drive i missatgeria instantània.',
    },
  ];

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="px-4 sm:px-6 lg:px-8 bg-white"
      style={{ paddingTop: '76px', paddingBottom: '76px' }}
      aria-labelledby="problem-heading"
    >
      <div className="max-w-[1280px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[40fr_60fr] gap-12 lg:gap-20 items-start">

          {/* Left — editorial heading */}
          <div className="lg:sticky lg:top-28" style={revealStyle(visible, 0)}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-5 h-px bg-slate-400" aria-hidden="true" />
              <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-slate-500" style={{ fontFamily: 'var(--font-dm-sans), system-ui, sans-serif' }}>PROBLEMA</span>
            </div>
            <h2
              id="problem-heading"
              className="font-semibold text-slate-900 tracking-[-0.02em] text-balance"
              style={{
                ...headingFont,
                fontSize: 'clamp(1.75rem, 3vw, 2.75rem)',
                lineHeight: '1.06',
              }}
            >
              La preparació d&apos;un ple implica reconstruir informació dispersa.
            </h2>
            <p className="text-[1.0625rem] text-slate-600 leading-relaxed mt-4 max-w-sm">
              El repte no és només trobar els documents, sinó connectar-los, verificar-los i convertir-los en context útil abans de la sessió.
            </p>
          </div>

          {/* Right — numbered rows */}
          <div className="divide-y divide-slate-200 border-t border-slate-200" style={revealStyle(visible, 60)}>
            {problems?.map((p, i) => (
              <div key={p?.key} className="grid grid-cols-[3.5rem_1fr] gap-6 py-5 items-start" style={revealStyle(visible, 60 + i * 40)}>
                <span className="text-[2rem] font-bold font-mono text-slate-200 leading-none tracking-tight" style={headingFont}>{p?.num}</span>
                <div>
                  <h3 className="text-[1.0625rem] font-semibold text-slate-900 mb-1.5 leading-snug">{p?.title}</h3>
                  <p className="text-[0.9375rem] text-slate-600 leading-relaxed">{p?.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Features section ─────────────────────────────────────────────────

function FeaturesSection() {
  const { ref, visible } = useReveal();

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      id="funcionalitats"
      className="px-4 sm:px-6 lg:px-8"
      style={{ paddingTop: '76px', paddingBottom: '76px', backgroundColor: '#f8f9fb' }}
      aria-labelledby="features-heading"
    >
      <div className="max-w-[1280px] mx-auto">
        <div className="mb-9" style={revealStyle(visible, 0)}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-5 h-px bg-slate-400" aria-hidden="true" />
            <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-slate-500" style={{ fontFamily: 'var(--font-dm-sans), system-ui, sans-serif' }}>FUNCIONALITATS</span>
          </div>
          <h2
            id="features-heading"
            className="font-semibold text-slate-900 tracking-[-0.02em] mb-2.5"
            style={{
              ...headingFont,
              fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
              lineHeight: '1.06',
            }}
          >
            Funcionalitats principals
          </h2>
          <p className="text-[1.0625rem] text-slate-600 max-w-xl leading-relaxed">
            Dossiers, citacions, redacció i seguiment dins d&apos;un mateix flux de treball.
          </p>
        </div>

        {/* Asymmetric 60/40 layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[60fr_40fr] gap-5">

          {/* Protagonist — Dossier verificable */}
          <div
            className="p-7 sm:p-8 rounded-[10px] bg-white border border-slate-200 flex flex-col transition-all duration-200 hover:border-slate-300 hover:shadow-sm"
            style={revealStyle(visible, 40)}
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-400" style={{ fontFamily: 'var(--font-dm-sans), system-ui, sans-serif' }}>FUNCIONALITAT PRINCIPAL</span>
            </div>
            <h3 className="font-bold text-slate-900 mb-3 tracking-tight" style={{ ...headingFont, fontSize: '1.375rem', lineHeight: '1.2' }}>Dossier verificable</h3>
            <p className="text-[0.9375rem] text-slate-600 leading-relaxed mb-6">
              Organitza cada punt de l&apos;ordre del dia amb resum, imports, actors i antecedents. Visualitza el percentatge de revisió i les alertes documentals d&apos;un cop d&apos;ull.
            </p>

            {/* Product representation */}
            <div className="mt-auto space-y-0 divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden" aria-hidden="true">
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50">
                <span className="text-[10px] font-mono font-semibold text-slate-500 uppercase tracking-widest">SESSIÓ · Ple Ordinari Agost 2026</span>
                <span className="text-[10px] font-mono text-slate-400">33% revisat</span>
              </div>
              {[
                { num: '01', label: "Aprovació de l'acta anterior", status: 'REVISAT', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
                { num: '02', label: 'Modificació pressupostària 2/2026', status: 'ALERTA', color: 'text-amber-700 bg-amber-50 border-amber-200', alert: true },
                { num: '03', label: 'Ordenança reguladora de terrasses', status: 'EN REVISIÓ', color: 'text-amber-700 bg-amber-50 border-amber-200' },
                { num: '04', label: 'Adjudicació neteja viària 2026–2029', status: 'PENDENT', color: 'text-slate-500 bg-slate-50 border-slate-200' },
                { num: '05', label: 'Conveni Diputació de Barcelona', status: 'PENDENT', color: 'text-slate-500 bg-slate-50 border-slate-200' },
              ]?.map((item) => (
                <div key={item?.label} className={`flex items-center gap-3 px-4 py-2.5 bg-white ${item?.alert ? 'border-l-2 border-l-amber-400' : ''}`}>
                  <span className="text-[10px] font-mono text-slate-400 w-5 flex-shrink-0">{item?.num}</span>
                  <span className="text-[12px] text-slate-700 flex-1 min-w-0 truncate">{item?.label}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded border font-semibold font-mono tracking-wide flex-shrink-0 ${item?.color}`}>{item?.status}</span>
                </div>
              ))}
              <div className="flex items-center justify-between px-4 py-2 bg-slate-50">
                <span className="text-[10px] font-mono text-slate-400">FONT · Convocatòria oficial</span>
                <span className="text-[10px] font-mono text-emerald-600 font-semibold">FONT VERIFICADA</span>
              </div>
            </div>
          </div>

          {/* Secondary features — compact */}
          <div className="flex flex-col gap-4">
            {[
              {
                key: 'f2',
                icon: Link2,
                iconBg: 'rgba(16,185,129,0.08)',
                iconColor: 'text-emerald-700',
                label: 'FONT',
                title: 'Citacions i fonts',
                text: 'Relaciona cada dada amb la pàgina i el fragment del document original. Distingeix entre dades extretes i inferències.',
              },
              {
                key: 'f3',
                icon: PenLine,
                iconBg: 'rgba(245,158,11,0.08)',
                iconColor: 'text-amber-700',
                label: 'REVISAT',
                title: 'Redacció amb revisió humana',
                text: 'Prepara preguntes, precs i mocions sense substituir la revisió humana. Tots els esborranys requereixen validació explícita.',
              },
              {
                key: 'f4',
                icon: FolderOpen,
                iconBg: 'rgba(30,58,95,0.08)',
                iconColor: '',
                iconStyle: { color: 'var(--primary)' },
                label: 'SEGUIMENT',
                title: 'Seguiment municipal i territorial',
                text: 'Conserva responsables, terminis i documents en un únic espai. Vincula cada assumpte als plens i esborranys relacionats.',
              },
            ]?.map((f, i) => {
              const FIcon = f?.icon;
              return (
                <div
                  key={f?.key}
                  className="p-5 rounded-[10px] bg-white border border-slate-200 flex flex-col flex-1 transition-all duration-200 hover:border-slate-300 hover:shadow-sm"
                  style={revealStyle(visible, 80 + i * 40)}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: f?.iconBg }} aria-hidden="true">
                      <FIcon size={15} className={f?.iconColor} style={f?.iconStyle} aria-hidden="true" />
                    </div>
                    <span className="text-[10px] font-mono font-semibold tracking-[0.15em] uppercase text-slate-400 border border-slate-200 px-1.5 py-0.5 rounded">{f?.label}</span>
                  </div>
                  <h3 className="text-[1rem] font-bold text-slate-900 mb-2 leading-snug">{f?.title}</h3>
                  <p className="text-[0.875rem] text-slate-600 leading-relaxed">{f?.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Navy Brand Moment ────────────────────────────────────────────────

function NavyBrandSection() {
  const { ref, visible } = useReveal();
  const steps = [
    {
      key: 'nb-1',
      num: '01',
      title: 'Incorpora',
      text: 'Afegeix la convocatòria, les propostes i els annexos del ple.',
    },
    {
      key: 'nb-2',
      num: '02',
      title: 'Revisa',
      text: 'Cada punt apareix amb resum, dades extretes, citacions verificables i alertes.',
    },
    {
      key: 'nb-3',
      num: '03',
      title: 'Actua',
      text: 'Redacta actuacions, registra compromisos i fes-ne seguiment des d\'un únic espai.',
    },
  ];

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      id="com-funciona"
      className="px-4 sm:px-6 lg:px-8"
      style={{ paddingTop: '76px', paddingBottom: '76px', backgroundColor: 'var(--primary)' }}
      aria-labelledby="navy-heading"
    >
      <div className="max-w-[1280px] mx-auto">
        {/* Eyebrow — DM Sans, not font-mono */}
        <div className="flex items-center gap-3 mb-6" style={revealStyle(visible, 0)}>
          <div className="w-5 h-px bg-white/30" aria-hidden="true" />
          <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-white/50" style={{ fontFamily: 'var(--font-dm-sans), system-ui, sans-serif' }}>PROCÉS</span>
        </div>

        <h2
          id="navy-heading"
          className="font-semibold text-white tracking-[-0.02em] mb-10 text-balance max-w-2xl"
          style={{
            ...headingFont,
            fontSize: 'clamp(1.75rem, 3vw, 2.75rem)',
            lineHeight: '1.06',
            ...revealStyle(visible, 40),
          }}
        >
          Del document dispers a una actuació preparada.
        </h2>

        {/* 3-step process — horizontal desktop */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-white/10">
          {steps?.map((step, i) => (
            <div key={step?.key} className="px-0 md:px-10 py-6 md:py-0 first:pl-0 last:pr-0" style={revealStyle(visible, 80 + i * 50)}>
              <div className="flex items-baseline gap-4 mb-4">
                <span className="font-bold font-mono text-white/20 leading-none" style={{ ...headingFont, fontSize: '3rem' }}>{step?.num}</span>
                <div className="w-8 h-px bg-white/20 flex-shrink-0" aria-hidden="true" />
              </div>
              <h3 className="font-semibold text-white mb-2.5 tracking-tight" style={{ ...headingFont, fontSize: '1.25rem', lineHeight: '1.2' }}>{step?.title}</h3>
              <p className="text-[0.9375rem] text-white/65 leading-relaxed">{step?.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── For Whom section ─────────────────────────────────────────────────

function ForWhomSection() {
  const { ref, visible } = useReveal();

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      id="per-a-qui"
      className="px-4 sm:px-6 lg:px-8 bg-white"
      style={{ paddingTop: '76px', paddingBottom: '76px' }}
      aria-labelledby="for-whom-heading"
    >
      <div className="max-w-[1280px] mx-auto">
        <div className="mb-9" style={revealStyle(visible, 0)}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-5 h-px bg-slate-400" aria-hidden="true" />
            <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-slate-500" style={{ fontFamily: 'var(--font-dm-sans), system-ui, sans-serif' }}>PER A QUI</span>
          </div>
          <h2
            id="for-whom-heading"
            className="font-semibold text-slate-900 tracking-[-0.02em] text-balance"
            style={{
              ...headingFont,
              fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
              lineHeight: '1.06',
            }}
          >
            Un mateix sistema de treball,<br className="hidden sm:block" /> del municipi a l&apos;organització.
          </h2>
        </div>

        {/* Two editorial columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-slate-200 rounded-[10px] overflow-hidden" style={revealStyle(visible, 60)}>
          <div className="p-7 sm:p-8 border-b md:border-b-0 md:border-r border-slate-200 transition-colors duration-200 hover:bg-slate-50/50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono font-semibold tracking-[0.15em] uppercase text-slate-400 border border-slate-200 px-1.5 py-0.5 rounded">GRUP MUNICIPAL</span>
            </div>
            <h3 className="font-bold text-slate-900 mb-3 mt-3 tracking-tight" style={{ ...headingFont, fontSize: '1.375rem', lineHeight: '1.2' }}>Per als grups municipals</h3>
            <p className="text-[0.9375rem] text-slate-600 leading-relaxed">
              Preparar sessions, recuperar antecedents, redactar actuacions i seguir compromisos des d&apos;un únic espai de treball.
            </p>
            <div className="mt-5 space-y-2">
              {['Preparació de sessions', 'Recuperació d\'antecedents', 'Redacció d\'actuacions', 'Seguiment de compromisos']?.map((item) => (
                <div key={item} className="flex items-center gap-2.5 text-[0.875rem] text-slate-600">
                  <div className="w-1 h-1 rounded-full bg-slate-400 flex-shrink-0" aria-hidden="true" />
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="p-7 sm:p-8 transition-colors duration-200 hover:bg-slate-50/50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono font-semibold tracking-[0.15em] uppercase text-slate-400 border border-slate-200 px-1.5 py-0.5 rounded">ORGANITZACIÓ</span>
            </div>
            <h3 className="font-bold text-slate-900 mb-3 mt-3 tracking-tight" style={{ ...headingFont, fontSize: '1.375rem', lineHeight: '1.2' }}>Per a les organitzacions</h3>
            <p className="text-[0.9375rem] text-slate-600 leading-relaxed">
              Donar als grups municipals un sistema de treball comú, conservar memòria institucional i facilitar la coordinació territorial.
            </p>
            <div className="mt-5 space-y-2">
              {['Sistema de treball comú', 'Memòria institucional', 'Coordinació territorial', 'Múltiples municipis']?.map((item) => (
                <div key={item} className="flex items-center gap-2.5 text-[0.875rem] text-slate-600">
                  <div className="w-1 h-1 rounded-full bg-slate-400 flex-shrink-0" aria-hidden="true" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Trust section ────────────────────────────────────────────────────

function TrustSection() {
  const { ref, visible } = useReveal();
  const keyPoints = [
    {
      key: 'trust-1',
      label: 'DECISIÓ POLÍTICA',
      title: 'La plataforma no pren decisions polítiques',
      text: 'PlenariCAT organitza i estructura informació. Les decisions polítiques, les valoracions i els vots corresponen exclusivament als representants electes.',
    },
    {
      key: 'trust-2',
      label: 'ESBORRANY',
      title: 'Tot el contingut generat és un esborrany',
      text: 'Cap text produït per PlenariCAT es presenta com a definitiu. Cada esborrany requereix revisió i validació explícita per part de l\'usuari.',
    },
    {
      key: 'trust-3',
      label: 'TRAÇABILITAT',
      title: 'Inferències diferenciades de dades extretes',
      text: 'El sistema distingeix visualment entre dades extretes directament dels documents i inferències del sistema, que sempre es marquen com a pendents de verificació.',
    },
  ];

  const secondaryPoints = [
    {
      key: 'trust-4',
      title: 'Citacions que permeten comprovar la font',
      text: 'Cada dada inclou la referència al document original, la pàgina i el fragment exacte.',
    },
    {
      key: 'trust-5',
      title: 'Dades de la demostració simulades',
      text: 'Totes les dades d\'aquest entorn de demostració són fictícies. Cap dada real de cap ajuntament és accessible ni emmagatzemada.',
    },
    {
      key: 'trust-6',
      title: 'Sense perfilat de ciutadans',
      text: 'PlenariCAT és una eina de treball intern per a representants municipals. No recull ni processa dades de ciutadans.',
    },
  ];

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      id="seguretat"
      className="px-4 sm:px-6 lg:px-8"
      style={{ paddingTop: '76px', paddingBottom: '76px', backgroundColor: '#f8f9fb' }}
      aria-labelledby="trust-heading"
    >
      <div className="max-w-[1280px] mx-auto">
        <div className="flex items-start gap-4 mb-9" style={revealStyle(visible, 0)}>
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
            style={{ backgroundColor: 'rgba(30,58,95,0.08)' }}
            aria-hidden="true"
          >
            <ShieldCheck size={17} style={{ color: 'var(--primary)' }} />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-2.5">
              <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-slate-500" style={{ fontFamily: 'var(--font-dm-sans), system-ui, sans-serif' }}>TRANSPARÈNCIA</span>
            </div>
            <h2
              id="trust-heading"
              className="font-semibold text-slate-900 tracking-[-0.02em] mb-2.5"
              style={{
                ...headingFont,
                fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
                lineHeight: '1.06',
              }}
            >
              Transparència i límits del sistema
            </h2>
            <p className="text-[1.0625rem] text-slate-600 max-w-xl leading-relaxed">
              PlenariCAT és una eina de suport a la feina dels representants municipals, no un substitut del judici polític ni de la revisió humana.
            </p>
          </div>
        </div>

        {/* Key points — large typographic rows, min 16px body */}
        <div className="divide-y divide-slate-200 border-t border-slate-200 mb-8" style={revealStyle(visible, 40)}>
          {keyPoints?.map((p, i) => (
            <div key={p?.key} className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-4 sm:gap-8 py-5 items-start" style={revealStyle(visible, 40 + i * 40)}>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0" aria-hidden="true" />
                <span className="text-[10px] font-mono font-semibold tracking-[0.12em] uppercase text-slate-400 border border-slate-200 px-1.5 py-0.5 rounded">{p?.label}</span>
              </div>
              <div>
                <h3 className="text-[1.0625rem] font-semibold text-slate-900 mb-2 leading-snug">{p?.title}</h3>
                <p className="text-[1rem] text-slate-600 leading-relaxed max-w-2xl">{p?.text}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Secondary points — compact grid, min 14px body */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" style={revealStyle(visible, 160)}>
          {secondaryPoints?.map((p) => (
            <div key={p?.key} className="p-5 rounded-lg bg-white border border-slate-200 transition-all duration-200 hover:border-slate-300 hover:shadow-sm">
              <div className="flex items-start gap-2 mb-2">
                <CheckCircle2 size={13} className="text-emerald-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <h3 className="text-[0.9375rem] font-semibold text-slate-900 leading-snug">{p?.title}</h3>
              </div>
              <p className="text-[0.875rem] text-slate-600 leading-relaxed pl-5">{p?.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Pilot section ────────────────────────────────────────────────────

function PilotSection() {
  const { ref, visible } = useReveal();

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      id="pilot"
      className="px-4 sm:px-6 lg:px-8 bg-white"
      style={{ paddingTop: '76px', paddingBottom: '76px' }}
      aria-labelledby="pilot-heading"
    >
      <div className="max-w-[1280px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[50fr_50fr] gap-12 lg:gap-20 items-start">

          {/* Left */}
          <div style={revealStyle(visible, 0)}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-5 h-px bg-slate-400" aria-hidden="true" />
              <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-slate-500" style={{ fontFamily: 'var(--font-dm-sans), system-ui, sans-serif' }}>METODOLOGIA D&apos;IMPLANTACIÓ</span>
            </div>
            <h2
              id="pilot-heading"
              className="font-semibold text-slate-900 tracking-[-0.02em] mb-4 text-balance"
              style={{
                ...headingFont,
                fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
                lineHeight: '1.06',
              }}
            >
              Comença en 3–5 municipis. Escala després a l&apos;organització.
            </h2>
            <p className="text-[1.0625rem] text-slate-600 leading-relaxed mb-7">
              Comencem amb una demostració de 30 minuts. Si hi ha encaix, despleguem un pilot de 90 dies en 3–5 municipis amb objectius i mètriques acordats.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                href="/demo"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[0.9375rem] font-semibold text-white rounded-lg transition-all hover:opacity-90 hover:-translate-y-px min-h-[48px]"
                style={{ backgroundColor: 'var(--primary)' }}
              >
                Demana una demo de 30 minuts
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
              <Link
                href="/login"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[0.9375rem] font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all min-h-[48px]"
              >
                Explora la demostració
              </Link>
            </div>
          </div>

          {/* Right — typographic figures */}
          <div className="space-y-0 divide-y divide-slate-200 border-t border-b border-slate-200" style={revealStyle(visible, 60)}>
            {[
              {
                key: 'p1',
                figure: '30',
                unit: 'minuts',
                label: 'de demo',
                text: 'Una demostració de 30 minuts per veure l\'encaix del producte.',
              },
              {
                key: 'p2',
                figure: '3–5',
                unit: 'municipis',
                label: 'de pilot',
                text: 'Un abast acotat per provar el flux en situacions reals amb mètriques acordades.',
              },
              {
                key: 'p3',
                figure: '90',
                unit: 'dies',
                label: 'de pilot',
                text: 'Sense compromís de continuïtat. Els resultats determinen si té sentit ampliar el desplegament.',
              },
            ]?.map((item) => (
              <div key={item?.key} className="grid grid-cols-[6rem_1fr] gap-6 py-5 items-start">
                <div>
                  <span className="font-bold text-slate-900 leading-none block" style={{ ...headingFont, fontSize: '2.25rem' }}>{item?.figure}</span>
                  <span className="text-[11px] font-mono text-slate-500 uppercase tracking-wide">{item?.unit}</span>
                </div>
                <div>
                  <span className="text-[10px] font-mono font-semibold tracking-[0.12em] uppercase text-slate-400 border border-slate-200 px-1.5 py-0.5 rounded mb-2 inline-block">{item?.label}</span>
                  <p className="text-[0.9375rem] text-slate-600 leading-relaxed mt-1">{item?.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── FAQ section — 40/60 editorial layout ────────────────────────────

function FAQSection() {
  const { ref, visible } = useReveal();
  const faqs = [
    {
      key: 'faq-1',
      q: 'La plataforma publica contingut automàticament?',
      a: 'No. Tot el contingut generat és un esborrany i requereix revisió humana.',
    },
    {
      key: 'faq-2',
      q: 'PlenariCAT pren decisions polítiques?',
      a: 'No. Estructura informació i facilita la feina documental. Les decisions i validacions corresponen sempre a les persones usuàries.',
    },
    {
      key: 'faq-3',
      q: 'Pot treballar amb diversos municipis?',
      a: 'Sí. Els espais municipals es mantenen diferenciats dins de cada entorn organitzatiu.',
    },
    {
      key: 'faq-4',
      q: 'Quines dades utilitza la demostració?',
      a: 'La demostració utilitza dades simulades i no correspon a informació operativa real d\'un ajuntament.',
    },
    {
      key: 'faq-5',
      q: 'Com funciona el pilot?',
      a: 'Es planteja durant 90 dies amb 3–5 municipis i criteris d\'avaluació acordats prèviament.',
    },
    {
      key: 'faq-6',
      q: 'Es pot desplegar a tota una organització?',
      a: 'Sí. PlenariCAT està plantejat perquè una organització pugui treballar amb múltiples espais municipals diferenciats dins d\'un mateix entorn, mantenint separat el context de cada municipi.',
    },
  ];

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="px-4 sm:px-6 lg:px-8"
      style={{ paddingTop: '64px', paddingBottom: '64px', backgroundColor: '#f8f9fb' }}
      aria-labelledby="faq-heading"
    >
      <div className="max-w-[1280px] mx-auto">
        {/* 40/60 editorial layout on desktop, single column on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-[40fr_60fr] gap-12 lg:gap-20 items-start">

          {/* Left — eyebrow + heading */}
          <div className="lg:sticky lg:top-28" style={revealStyle(visible, 0)}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-5 h-px bg-slate-400" aria-hidden="true" />
              <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-slate-500" style={{ fontFamily: 'var(--font-dm-sans), system-ui, sans-serif' }}>FAQ</span>
            </div>
            <h2
              id="faq-heading"
              className="font-semibold text-slate-900 tracking-[-0.02em]"
              style={{
                ...headingFont,
                fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
                lineHeight: '1.06',
              }}
            >
              Preguntes freqüents
            </h2>
          </div>

          {/* Right — 6 questions with horizontal dividers, no cards, no JS accordion */}
          <div className="divide-y divide-slate-200 border-t border-slate-200" style={revealStyle(visible, 60)}>
            {faqs?.map((faq) => (
              <div key={faq?.key} className="py-5">
                <h3 className="text-[1rem] font-semibold text-slate-900 mb-1.5 leading-snug">{faq?.q}</h3>
                <p className="text-[0.9375rem] text-slate-600 leading-relaxed">{faq?.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Final CTA ────────────────────────────────────────────────────────

function FinalCTA() {
  const { ref, visible } = useReveal();

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="px-4 sm:px-6 lg:px-8 bg-white"
      style={{ paddingTop: '64px', paddingBottom: '64px' }}
      aria-labelledby="cta-heading"
    >
      <div className="max-w-[1280px] mx-auto">
        <div
          className="rounded-[10px] px-8 py-10 sm:px-14 sm:py-12 lg:px-20 lg:py-14 text-center max-w-3xl mx-auto"
          style={{ backgroundColor: 'var(--primary)', ...revealStyle(visible, 0) }}
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-5 h-px bg-white/30" aria-hidden="true" />
            <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-white/50" style={{ fontFamily: 'var(--font-dm-sans), system-ui, sans-serif' }}>PLENARICAT</span>
            <div className="w-5 h-px bg-white/30" aria-hidden="true" />
          </div>
          <h2
            id="cta-heading"
            className="font-semibold text-white mb-3.5 text-balance tracking-[-0.02em]"
            style={{
              ...headingFont,
              fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
              lineHeight: '1.06',
            }}
          >
            Vols veure com encaixa PlenariCAT en el vostre flux de treball?
          </h2>
          <p className="text-white/70 mb-8 max-w-sm mx-auto leading-relaxed text-[1.0625rem]">
            Comencem amb una demostració de 30 minuts. Sense compromís.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              href="/demo"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white font-semibold rounded-lg transition-all hover:bg-slate-50 hover:-translate-y-px min-h-[48px] text-[0.9375rem]"
              style={{ color: 'var(--primary)' }}
            >
              Demana una demo de 30 minuts
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
            <Link
              href="/login"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white/10 text-white font-medium rounded-lg hover:bg-white/20 transition-colors min-h-[48px] border border-white/20 text-[0.9375rem]"
            >
              Explora la demostració
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────

function LandingFooter() {
  return (
    <footer className="border-t border-slate-200 bg-white py-10 px-4 sm:px-6 lg:px-8" role="contentinfo">
      <div className="max-w-[1280px] mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div>
          {/* Brand lockup — navy icon + typographic lockup */}
          <div className="flex items-center gap-2.5 mb-2">
            <div
              className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'var(--primary)' }}
              aria-hidden="true"
            >
              <Building2 size={13} className="text-white" />
            </div>
            <div>
              <span className="text-[17px] font-semibold text-slate-900 leading-tight block tracking-[-0.03em]" style={headingFont}>PlenariCAT</span>
              <span className="text-[11px] text-slate-500 leading-tight block" style={{ fontFamily: 'var(--font-dm-sans), system-ui, sans-serif' }}>Treball municipal</span>
            </div>
          </div>
          <p className="text-xs text-slate-500 mb-1">Plataforma de treball municipal per a organitzacions polítiques</p>
          <p className="text-xs text-slate-400">
            <span className="font-mono">plenari.cat</span>
            {' '}· Treballa amb fonts. Decideix amb criteri. · Entorn de demostració
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Contacte comercial:{' '}
            <a
              href="mailto:demo@plenari.cat"
              className="text-slate-500 hover:text-slate-700 transition-colors"
            >
              demo@plenari.cat
            </a>
          </p>
        </div>

        <nav aria-label="Navegació del peu de pàgina">
          <ul className="flex flex-wrap gap-x-5 gap-y-2">
            {[
              { label: 'Accessibilitat', href: '/accessibilitat' },
              { label: 'Privacitat', href: '/privacitat' },
              { label: 'Avís legal', href: '/avis-legal' },
              { label: 'Cookies', href: '/cookies' },
            ]?.map((item) => (
              <li key={item?.label}>
                <Link href={item?.href} className="text-xs text-slate-500 hover:text-slate-900 transition-colors">
                  {item?.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </footer>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────

export default function LandingPage() {
  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-slate-900 focus:rounded-lg focus:shadow-lg focus:text-sm focus:font-medium"
      >
        Salta al contingut principal
      </a>
      <LandingHeader />
      <main id="main-content" tabIndex={-1}>
        <HeroSection />
        <ProblemSection />
        <FeaturesSection />
        <NavyBrandSection />
        <ForWhomSection />
        <TrustSection />
        <PilotSection />
        <FAQSection />
        <FinalCTA />
      </main>
      <LandingFooter />
      <HomeIntroSplash />
    </>
  );
}